import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { set_password as set_password_call, verify_magic_link } from '../api/magic_link_client';
import { get_welcome_translations, resolve_locale } from '../i18n/welcome_translations';
import { read_token, validate_password } from '../lib/welcome_logic';
import { AuthCard } from '../ui/auth_card';
import { TextField } from '../ui/text_field';

type Phase = 'verifying' | 'form' | 'submitting' | 'success' | 'invalid';

/**
 * /welcome/set-password?token=… — the email link target. Verifies the token,
 * shows the username, and lets the customer set their first password. When
 * the native member app is installed and the domain is associated, the OS
 * opens the app instead and this page never renders.
 */
export function WelcomeSetPasswordRoute(): ReactNode {
  // Captured once at mount: the effect strips the token from the URL via
  // replaceState, so re-reading window.location.search on later renders would
  // yield '' and recompute `token` to null — re-firing the effect and
  // clobbering the verified `form` phase with `invalid`.
  const [search] = useState(() => window.location.search);
  const t = useMemo(() => get_welcome_translations(resolve_locale(search)), [search]);
  const token = useMemo(() => read_token(search), [search]);
  const app_url = (import.meta.env['VITE_MEMBER_APP_URL'] as string | undefined) ?? '';

  const [phase, set_phase] = useState<Phase>('verifying');
  const [username, set_username] = useState('');
  const [password, set_password_value] = useState('');
  const [password_repeat, set_password_repeat] = useState('');
  const [error, set_error] = useState<string | null>(null);

  useEffect(() => {
    if (token === null) {
      set_phase('invalid');
      return;
    }
    // Strip the token from the URL bar (no history/referrer leak).
    window.history.replaceState({}, '', window.location.pathname);
    void verify_magic_link(token)
      .then((res) => {
        if (res.valid === true && res.purpose === 'initial_register') {
          set_username(res.username ?? '');
          set_phase('form');
        } else {
          set_phase('invalid');
        }
      })
      .catch(() => set_phase('invalid'));
  }, [token]);

  async function on_submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (token === null) return;
    const validation = validate_password(password, password_repeat);
    if (validation !== null) {
      set_error(t[validation]);
      return;
    }
    set_error(null);
    set_phase('submitting');
    try {
      await set_password_call({ token, password, password_repeat });
      set_phase('success');
    } catch (e) {
      const code = e instanceof Error ? e.message : '';
      // Token died between verify and submit → treat as invalid link.
      if (code === 'BAD_REQUEST') {
        set_phase('invalid');
        return;
      }
      // Backend password-policy rejection (kernel returns VALIDATION_ERROR / 422).
      set_error(code === 'VALIDATION_ERROR' ? t.error_password_policy : t.error_unexpected);
      set_phase('form');
    }
  }

  if (phase === 'verifying') {
    return (
      <AuthCard title={t.set_heading}>
        <div className="uf-loading">
          <span className="uf-loading__dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          {t.verifying}
        </div>
      </AuthCard>
    );
  }

  if (phase === 'invalid') {
    return (
      <AuthCard title={t.link_invalid_heading}>
        <p className="uf-lead">{t.link_invalid_message}</p>
        <a className="uf-btn uf-btn--ghost" href="/welcome">
          {t.request_new_link}
        </a>
      </AuthCard>
    );
  }

  if (phase === 'success') {
    return (
      <AuthCard title={t.set_success_heading}>
        <p className="uf-lead">{t.set_success_message}</p>
        <div className="uf-user">
          <span className="uf-user__label">{t.username_label}</span>
          <span className="uf-user__value">{username}</span>
        </div>
        {app_url !== '' ? (
          <a className="uf-btn" href={app_url}>
            {t.open_in_app}
          </a>
        ) : null}
      </AuthCard>
    );
  }

  // phase === 'form' | 'submitting'
  const is_submitting = phase === 'submitting';

  return (
    <AuthCard title={t.set_heading}>
      <p className="uf-lead">{t.set_description}</p>
      <div className="uf-user">
        <span className="uf-user__label">{t.username_label}</span>
        <span className="uf-user__value">{username}</span>
      </div>
      <form className="uf-form" onSubmit={on_submit}>
        <TextField
          label={t.password_label}
          value={password}
          on_change={set_password_value}
          type="password"
          autocomplete="new-password"
          disabled={is_submitting}
        />
        <TextField
          label={t.password_repeat_label}
          value={password_repeat}
          on_change={set_password_repeat}
          type="password"
          autocomplete="new-password"
          disabled={is_submitting}
          on_enter={() => {
            if (is_submitting === false) {
              void on_submit(new Event('submit') as unknown as FormEvent);
            }
          }}
        />
        {error !== null ? (
          <p className="uf-alert" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" className="uf-btn" disabled={is_submitting}>
          {is_submitting === true ? t.submitting : t.set_submit}
        </button>
      </form>
    </AuthCard>
  );
}
